'use client';

/**
 * WebLandingFoundersCard — landing-page founders section.
 *
 * Two profiles in a 2-column grid: name + role + italic blockquote +
 * bio. Compact web variant — distinct from the deck's deeper Person
 * model with credential lists. Content via `web_landing_founders`.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, XCircle } from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebFoundersContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';


export function WebLandingFoundersCard() {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', 'web_landing_founders'),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebFoundersContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebFoundersContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load founders');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  return (
    <section className="web-surface relative py-24 px-6 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto">
        <motion.h2
          className={cn(T.name, 'text-center mb-16')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {data.headline}
        </motion.h2>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {data.founders.map((f, i) => (
            <motion.div
              key={f.name}
              className="rounded-xl border border-border bg-card/50 p-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
            >
              <h3 className="text-lg font-bold text-foreground">{f.name}</h3>
              <p className="text-sm text-primary font-medium mb-4">{f.title}</p>
              {f.quote && (
                <blockquote className="text-sm italic text-muted-foreground leading-relaxed mb-4 border-l-2 border-primary/30 pl-4">
                  &ldquo;{f.quote}&rdquo;
                </blockquote>
              )}
              <p className="text-sm text-muted-foreground leading-relaxed">{f.bio}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
